/* eslint-disable @typescript-eslint/no-floating-promises, no-plusplus, no-shadow, default-case */
import { useCallback, useRef } from 'react';
import { VideoEffectStore } from '../store/videoEffectModel';
import { BackgroundImage, BlurEffect, EffectIntent, VideoEffectPreferencesPayload } from '../videoEffectTypes';

function buildPreferencePayload(intent: EffectIntent): VideoEffectPreferencesPayload {
  if (intent.type === 'background' && intent.image !== null) {
    return {
      blurEffect: null,
      backgroundImage: {
        id: intent.image.id,
        filePath: intent.image.filePath,
        preExisting: intent.image.preExisting,
        url: intent.image.url,
      },
    };
  }

  if (intent.type === 'blur' && intent.effect !== 'none') {
    return {
      blurEffect: intent.effect,
      backgroundImage: null,
    };
  }

  return {
    blurEffect: null,
    backgroundImage: null,
  };
}

export interface UseVideoEffectDispatchParams {
  blurApplyEffect: (effect: BlurEffect) => Promise<void>;
  backgroundApplyBackground: (image: BackgroundImage | null, options?: { force?: boolean }) => Promise<void>;
}

export interface UseVideoEffectDispatchReturn {
  applyBlur: (effect: BlurEffect) => Promise<void>;
  applyBackground: (image: BackgroundImage | null) => Promise<void>;
  removeAllEffects: () => Promise<void>;
  activeBackgroundImageRef: React.MutableRefObject<BackgroundImage | null>;
}

export function useVideoEffectDispatch({
  blurApplyEffect,
  backgroundApplyBackground,
}: UseVideoEffectDispatchParams): UseVideoEffectDispatchReturn {
  const participantId = VideoEffectStore.useStoreState((actions) => actions.participantId);
  const activeBackgroundId = VideoEffectStore.useStoreState((state) => state.activeBackgroundId);
  const activeBlurEffect = VideoEffectStore.useStoreState((state) => state.activeBlurEffect);

  const setPreference = VideoEffectStore.useStoreActions((actions) => actions.savePreferences);

  const refs = useRef({
    setPreference,
    participantId,
    blurApplyEffect,
    blurCurrentEffect: activeBlurEffect,
    backgroundApplyBackground,
    activeBackgroundId,
  });

  refs.current.setPreference = setPreference;
  refs.current.participantId = participantId;
  refs.current.blurApplyEffect = blurApplyEffect;
  refs.current.blurCurrentEffect = activeBlurEffect;
  refs.current.backgroundApplyBackground = backgroundApplyBackground;
  refs.current.activeBackgroundId = activeBackgroundId;

  const activeBackgroundImageRef = useRef<BackgroundImage | null>(null);

  const operationGeneration = useRef(0);
  const pendingOperation = useRef<Promise<void>>(Promise.resolve());

  const persistIfLatest = useCallback((generation: number, intent: EffectIntent) => {
    if (generation !== operationGeneration.current) return;

    const { participantId: pid, setPreference: set } = refs.current;
    if (pid) {
      set({ preferences: buildPreferencePayload(intent) });
    }
  }, []);

  const dispatch = useCallback(
    (intent: EffectIntent): Promise<void> => {
      const generation = ++operationGeneration.current;

      const operation = pendingOperation.current.then(async () => {
        const { blurCurrentEffect, blurApplyEffect, activeBackgroundId, backgroundApplyBackground } = refs.current;

        switch (intent.type) {
          case 'blur': {
            if (activeBackgroundId !== null) {
              await backgroundApplyBackground(null);
            }
            await blurApplyEffect(intent.effect);
            break;
          }

          case 'background': {
            if (blurCurrentEffect !== 'none') {
              await blurApplyEffect('none');
            }
            await backgroundApplyBackground(intent.image, { force: true });
            activeBackgroundImageRef.current = intent.image;
            break;
          }

          case 'none': {
            await Promise.all([
              blurCurrentEffect !== 'none' ? blurApplyEffect('none') : Promise.resolve(),
              activeBackgroundId !== null ? backgroundApplyBackground(null) : Promise.resolve(),
            ]);
            activeBackgroundImageRef.current = null;
            break;
          }
        }

        persistIfLatest(generation, intent);
      });

      pendingOperation.current = operation.catch((err) => {
        console.error('Video effect operation failed:', err);
      });

      return pendingOperation.current;
    },
    [persistIfLatest],
  );

  const applyBlur = useCallback((effect: BlurEffect) => dispatch({ type: 'blur', effect }), [dispatch]);
  const applyBackground = useCallback((image: BackgroundImage | null) => dispatch({ type: 'background', image }), [dispatch]);
  const removeAllEffects = useCallback(() => dispatch({ type: 'none' }), [dispatch]);

  return { applyBlur, applyBackground, removeAllEffects, activeBackgroundImageRef };
}
